import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { WorkSprint } from "@torre/work-hours";
import Modal from "../../../components/base/Modal";
import { useTheme } from "../../../context/ThemeContext";
import { buildThemeClasses } from "../theme";
import ActivityCard from "../components/ActivityCard";
import ActividadFormModal from "../components/ActividadFormModal";
import ActividadDetailModal from "../components/ActividadDetailModal";
import { useCatalog, useSprints } from "../hooks/useActivityData";
import { useShowGithub } from "../hooks/useShowGithub";
import {
  activityErrorCode,
  activityErrorMessage,
  createSprint,
  fetchActivities,
  fetchSyncStatus,
  runGithubSync,
  updateActivity,
  updateSprint,
  type ActivitiesMeta,
  type ActivityRow,
  type SyncStatus,
} from "../services/activitiesService";
import { compareBacklog, filterGithub, isGithubActivity } from "../lib/format";
import { applyScope } from "../lib/activityScope";
import ActivityScopeFilters, { useActivityScope } from "../components/ActivityScopeFilters";

// ---------------------------------------------------------------------------
// Backlog y sprints (#1304): aquí se planea. Los dueños crean sprints por
// periodo, los inician (uno activo a la vez) y los cierran (lo no terminado
// vuelve al backlog). Cualquiera mueve tickets del backlog a un sprint.
// #1404: «Crear sprint» también está arriba, junto a «Nueva actividad», y el
// interruptor «Issues de GitHub» decide si el backlog los muestra: apagado,
// solo los tickets del equipo; encendido, además los issues con trabajo
// asociado (horas, sprint o un ticket propio ligado). Los demás nunca salen.
// ---------------------------------------------------------------------------

const SPRINT_TONE: Record<WorkSprint["status"], string> = {
  planned: "bg-sky-500/15 text-sky-600",
  active: "bg-emerald-500/15 text-emerald-600",
  closed: "bg-gray-400/15 text-gray-500",
};

export default function BacklogSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const { catalog } = useCatalog();
  const { sprints, activeSprintId, reload: reloadSprints } = useSprints();

  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [meta, setMeta] = useState<ActivitiesMeta | null>(null);
  const [epics, setEpics] = useState<ActivityRow[]>([]);
  const [allRows, setAllRows] = useState<ActivityRow[]>([]);
  const [showGithub, setShowGithub] = useShowGithub();
  const { scope, setScope, projects } = useActivityScope();
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<{ tone: "ok" | "warn" | "error"; text: string } | null>(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [targetSprint, setTargetSprint] = useState("");
  const [moving, setMoving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState<ActivityRow | null>(null);
  const [sprintForm, setSprintForm] = useState<{ name: string; goal: string; startDate: string; endDate: string } | null>(null);
  const [sprintError, setSprintError] = useState<string | null>(null);
  const [savingSprint, setSavingSprint] = useState(false);
  const [sync, setSync] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [backlog, all] = await Promise.all([fetchActivities({ backlog: true, limit: 2000 }), fetchActivities({ includeDone: true, limit: 2000 })]);
      setRows(backlog.data.filter((a) => !a.archived));
      setMeta(backlog.meta);
      setEpics(all.data.filter((a) => a.isEpic || all.data.some((b) => b.parentId === a.id)));
      setAllRows(all.data.filter((a) => !a.archived));
    } catch (err) {
      setNotice({ tone: "error", text: activityErrorMessage(err) ?? t("staffActivities.common.error") });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const canManage = Boolean(meta?.canManage);
  const isOwner = Boolean(meta?.isOwner);

  useEffect(() => {
    if (!canManage) return;
    fetchSyncStatus().then(setSync).catch(() => setSync(null));
  }, [canManage]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return applyScope(filterGithub(rows, allRows, showGithub), scope, projects)
      .filter((a) => !q || a.title.toLowerCase().includes(q) || a.key.toLowerCase().includes(q))
      .sort(compareBacklog);
  }, [rows, allRows, showGithub, search, scope, projects]);
  // Issues de GitHub del backlog que el interruptor deja fuera (para decir cuántos son).
  const githubTotal = useMemo(() => rows.filter(isGithubActivity).length, [rows]);
  const githubShown = useMemo(() => visible.filter(isGithubActivity).length, [visible]);
  // Tickets por sprint: lo que nace de las horas entra al sprint activo y aquí se ve cuánto lleva.
  const sprintCount = useMemo(() => {
    const out = new Map<string, number>();
    for (const a of allRows) if (a.sprintId) out.set(a.sprintId, (out.get(a.sprintId) ?? 0) + 1);
    return out;
  }, [allRows]);

  const today = new Date().toISOString().slice(0, 10);
  const openSprints = sprints.filter((s) => s.status !== "closed");
  const openSprintForm = () => {
    setSprintError(null);
    setSprintForm({ name: "", goal: "", startDate: today, endDate: today });
  };

  const toggle = (a: ActivityRow) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(a.id)) next.delete(a.id);
      else next.add(a.id);
      return next;
    });

  const [targetEpic, setTargetEpic] = useState("");
  const [assigning, setAssigning] = useState(false);
  // Colgar los seleccionados de una épica (#1304): se quedan en el backlog, ya con padre.
  const assignSelected = async () => {
    if (!targetEpic || selected.size === 0) return;
    setAssigning(true);
    setNotice(null);
    try {
      await Promise.all([...selected].map((id) => updateActivity(id, { parentId: targetEpic })));
      setRows((prev) => prev.map((a) => (selected.has(a.id) ? { ...a, parentId: targetEpic } : a)));
      setSelected(new Set());
      setNotice({ tone: "ok", text: t("staffActivities.epic.assigned", { count: selected.size }) });
    } catch (err) {
      setNotice({ tone: "error", text: activityErrorMessage(err) ?? t("staffActivities.common.error") });
    } finally {
      setAssigning(false);
    }
  };

  const moveSelected = async () => {
    if (!targetSprint || selected.size === 0) return;
    setMoving(true);
    setNotice(null);
    try {
      await Promise.all([...selected].map((id) => updateActivity(id, { sprintId: targetSprint })));
      setRows((prev) => prev.filter((a) => !selected.has(a.id)));
      setSelected(new Set());
      setNotice({ tone: "ok", text: t("staffActivities.backlog.moved") });
    } catch (err) {
      setNotice({ tone: "error", text: activityErrorMessage(err) ?? t("staffActivities.common.error") });
      void load();
    } finally {
      setMoving(false);
    }
  };

  const saveSprint = async () => {
    if (!sprintForm) return;
    setSprintError(null);
    if (!sprintForm.name.trim()) return setSprintError(t("staffActivities.backlog.sprintForm.errors.name"));
    if (!sprintForm.startDate || !sprintForm.endDate || sprintForm.startDate > sprintForm.endDate) return setSprintError(t("staffActivities.backlog.sprintForm.errors.dates"));
    setSavingSprint(true);
    try {
      await createSprint({ name: sprintForm.name.trim(), goal: sprintForm.goal.trim() || null, startDate: sprintForm.startDate, endDate: sprintForm.endDate });
      setSprintForm(null);
      reloadSprints();
    } catch (err) {
      setSprintError(activityErrorMessage(err) ?? t("staffActivities.backlog.sprintForm.errors.save"));
    } finally {
      setSavingSprint(false);
    }
  };

  const setSprintStatus = async (s: WorkSprint, status: "active" | "closed") => {
    setNotice(null);
    try {
      const res = await updateSprint(s.id, { status });
      if (status === "closed") setNotice({ tone: "ok", text: t("staffActivities.backlog.movedToBacklog", { count: res.movedToBacklog ?? 0 }) });
      reloadSprints();
      void load();
    } catch (err) {
      const code = activityErrorCode(err);
      setNotice({ tone: "error", text: code === "409" ? t("staffActivities.backlog.oneActive") : activityErrorMessage(err) ?? t("staffActivities.common.error") });
    }
  };

  const doSync = async () => {
    setSyncing(true);
    setNotice(null);
    try {
      const res = await runGithubSync();
      setSync(res);
      if (res.configured === false) setNotice({ tone: "warn", text: t("staffActivities.backlog.syncNotConfigured") });
      else if (res.error) setNotice({ tone: "error", text: t("staffActivities.backlog.syncError", { error: res.error }) });
      void load();
    } catch (err) {
      setNotice({ tone: "error", text: t("staffActivities.backlog.syncError", { error: activityErrorMessage(err) ?? "—" }) });
    } finally {
      setSyncing(false);
    }
  };

  const field = `rounded-lg border px-3 py-2 text-sm outline-none ${tc.border} ${tc.cardBg} ${tc.text}`;
  const label = `mb-1 block text-xs font-medium ${tc.textSecondary}`;

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-violet-500/15 text-violet-500">
              <i className="ri-list-check-3 text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>{t("staffActivities.backlog.title")}</h1>
              <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.backlog.subtitle")}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ActivityScopeFilters scope={scope} setScope={setScope} projects={projects} rows={rows} tc={tc} />
            <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.text}`} title={t("staffActivities.backlog.showGithubHint")}>
              <input
                type="checkbox"
                role="switch"
                aria-checked={showGithub}
                checked={showGithub}
                onChange={(e) => setShowGithub(e.target.checked)}
                className="h-4 w-4 accent-violet-500"
                data-testid="backlog-show-github"
              />
              <i className="ri-github-line" /> {t("staffActivities.backlog.showGithub")}
            </label>
            {canManage && (
              <button type="button" onClick={() => void doSync()} disabled={syncing} data-testid="backlog-sync" className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${tc.border} ${tc.text} ${tc.buttonHover} disabled:opacity-60`}>
                <i className={`ri-github-fill ${syncing ? "animate-spin" : ""}`} /> {syncing ? t("staffActivities.backlog.syncing") : t("staffActivities.backlog.sync")}
              </button>
            )}
            <button
              type="button"
              onClick={openSprintForm}
              disabled={!canManage}
              title={canManage ? undefined : t("staffActivities.backlog.onlyOwners")}
              data-testid="backlog-new-sprint"
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium ${tc.border} ${tc.text} ${tc.buttonHover} disabled:cursor-not-allowed disabled:opacity-60`}
            >
              <i className="ri-flag-2-line" /> {t("staffActivities.backlog.createSprint")}
            </button>
            <button type="button" onClick={() => setCreating(true)} className="flex items-center gap-2 rounded-lg bg-violet-600 px-3 py-2 text-sm font-medium text-white hover:bg-violet-500">
              <i className="ri-add-line" /> {t("staffActivities.kanban.newActivity")}
            </button>
          </div>
        </div>

        {canManage && sync && (
          <p className={`mb-3 text-xs ${sync.configured === false ? "text-amber-600" : tc.textSecondary}`} data-testid="backlog-sync-status">
            {sync.configured === false
              ? t("staffActivities.backlog.syncNotConfigured")
              : sync.lastRunAt
                ? t("staffActivities.backlog.syncLast", { date: new Date(sync.lastRunAt).toLocaleString("es-MX"), status: sync.status ?? "ok", created: sync.created ?? 0, updated: sync.updated ?? 0 })
                : t("staffActivities.backlog.syncNever")}
          </p>
        )}
        {notice && (
          <div className={`mb-4 rounded-lg border p-3 text-sm ${notice.tone === "ok" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-600" : notice.tone === "warn" ? "border-amber-500/30 bg-amber-500/10 text-amber-600" : "border-red-500/30 bg-red-500/10 text-red-500"}`} data-testid="backlog-notice">
            {notice.text}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Sprints */}
          <section className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className={`font-semibold ${tc.text}`}>{t("staffActivities.backlog.sprints")}</h2>
              {canManage && (
                <button type="button" data-testid="sprint-new" onClick={openSprintForm} className="rounded-lg bg-violet-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-violet-500">
                  <i className="ri-add-line" /> {t("staffActivities.backlog.newSprint")}
                </button>
              )}
            </div>
            {!canManage && <p className={`mb-2 text-[11px] ${tc.textSecondary}`}>{t("staffActivities.backlog.onlyOwners")}</p>}
            {sprints.length === 0 && <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.backlog.noSprints")}</p>}
            <ul className="space-y-2">
              {sprints.map((s) => (
                <li key={s.id} data-testid={`sprint-${s.id}`} className={`rounded-xl border p-3 ${tc.cardBorder} ${s.id === activeSprintId ? "ring-1 ring-emerald-500/60" : ""}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`font-medium ${tc.text}`}>{s.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${SPRINT_TONE[s.status]}`}>{t(`staffActivities.backlog.${s.status}`)}</span>
                  </div>
                  <p className={`text-xs ${tc.textSecondary}`}>
                    {s.startDate} → {s.endDate}
                    <span className="ml-2" data-testid={`sprint-count-${s.id}`}>
                      · {t("staffActivities.backlog.sprintTickets", { count: sprintCount.get(s.id) ?? 0 })}
                    </span>
                  </p>
                  {s.goal && (
                    <p className={`mt-1 text-xs ${tc.textSecondary}`}>
                      <span className="font-medium">{t("staffActivities.backlog.goal")}:</span> {s.goal}
                    </p>
                  )}
                  {canManage && s.status !== "closed" && (
                    <div className="mt-2 flex gap-2">
                      {s.status === "planned" && (
                        <button type="button" data-testid={`sprint-start-${s.id}`} onClick={() => void setSprintStatus(s, "active")} className="rounded-lg bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-500">
                          <i className="ri-play-line mr-1" />
                          {t("staffActivities.backlog.start")}
                        </button>
                      )}
                      {s.status === "active" && (
                        <button type="button" data-testid={`sprint-close-${s.id}`} onClick={() => void setSprintStatus(s, "closed")} className={`rounded-lg border px-2.5 py-1 text-xs ${tc.border} ${tc.text} ${tc.buttonHover}`}>
                          <i className="ri-flag-line mr-1" />
                          {t("staffActivities.backlog.close")}
                        </button>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>

          {/* Backlog */}
          <section className={`rounded-2xl border p-4 lg:col-span-2 ${tc.cardBorder} ${tc.cardBg}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className={`font-semibold ${tc.text}`}>
                {t("staffActivities.backlog.backlog")} <span className={`text-sm font-normal ${tc.textSecondary}`}>({visible.length})</span>
              </h2>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("staffActivities.backlog.search")} className={`${field} w-56 py-1.5 text-xs`} aria-label={t("staffActivities.backlog.search")} />
            </div>
            {selected.size > 0 && (
              <div className={`mb-3 flex flex-wrap items-center gap-2 rounded-xl border p-2 ${tc.cardBorder}`} data-testid="backlog-move-bar">
                <span className={`text-xs ${tc.text}`}>{t("staffActivities.backlog.selected", { count: selected.size })}</span>
                <select value={targetSprint} onChange={(e) => setTargetSprint(e.target.value)} className={`${field} py-1.5 text-xs`} aria-label={t("staffActivities.backlog.moveTo")} data-testid="backlog-move-select">
                  <option value="">{t("staffActivities.backlog.moveTo")}</option>
                  {openSprints.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({t(`staffActivities.backlog.${s.status}`)})
                    </option>
                  ))}
                </select>
                <button type="button" onClick={() => void moveSelected()} disabled={!targetSprint || moving} data-testid="backlog-move" className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-500 disabled:opacity-60">
                  {moving ? t("staffActivities.backlog.moving") : t("staffActivities.backlog.moveTo")}
                </button>
                <select value={targetEpic} onChange={(e) => setTargetEpic(e.target.value)} className={`${field} py-1.5 text-xs`} aria-label={t("staffActivities.epic.assignTo")} data-testid="backlog-epic-select">
                  <option value="">{t("staffActivities.epic.assignTo")}</option>
                  {epics
                    .filter((e) => e.isEpic)
                    .map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.key} · {e.title}
                      </option>
                    ))}
                </select>
                <button type="button" onClick={() => void assignSelected()} disabled={!targetEpic || assigning} data-testid="backlog-assign-epic" className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${tc.border} ${tc.text} ${tc.buttonHover} disabled:opacity-60`}>
                  {assigning ? t("staffActivities.epic.assigning") : t("staffActivities.epic.assign")}
                </button>
              </div>
            )}
            {githubTotal > 0 && (
              <p className={`mb-2 text-[11px] ${tc.textSecondary}`} data-testid="backlog-github-note">
                <i className="ri-github-line mr-1" />
                {showGithub ? t("staffActivities.backlog.githubShown", { shown: githubShown, total: githubTotal }) : t("staffActivities.backlog.githubHidden", { count: githubTotal })}
              </p>
            )}
            {loading && <p className={`text-sm ${tc.textSecondary}`}>{t("staffActivities.common.loading")}</p>}
            {!loading && visible.length === 0 && <p className={`py-6 text-center text-sm ${tc.textSecondary}`}>{t("staffActivities.backlog.backlogEmpty")}</p>}
            <div className="space-y-2">
              {visible.map((a) => (
                <ActivityCard key={a.id} activity={a} catalog={catalog} isOwner={isOwner} tc={tc} compact selected={selected.has(a.id)} onToggleSelect={toggle} onOpen={setDetail} />
              ))}
            </div>
          </section>
        </div>
      </div>

      {sprintForm && (
        <Modal
          title={t("staffActivities.backlog.newSprint")}
          themeClasses={tc}
          onClose={() => !savingSprint && setSprintForm(null)}
          width="max-w-lg"
          content={
            <div className="space-y-3" data-testid="sprint-form">
              <div>
                <label className={label}>{t("staffActivities.backlog.sprintForm.name")} *</label>
                <input value={sprintForm.name} onChange={(e) => setSprintForm({ ...sprintForm, name: e.target.value })} className={`${field} w-full`} aria-label={t("staffActivities.backlog.sprintForm.name")} />
              </div>
              <div>
                <label className={label}>{t("staffActivities.backlog.sprintForm.goal")}</label>
                <input value={sprintForm.goal} onChange={(e) => setSprintForm({ ...sprintForm, goal: e.target.value })} className={`${field} w-full`} aria-label={t("staffActivities.backlog.sprintForm.goal")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={label}>{t("staffActivities.backlog.sprintForm.start")} *</label>
                  <input type="date" value={sprintForm.startDate} onChange={(e) => setSprintForm({ ...sprintForm, startDate: e.target.value })} className={`${field} w-full`} aria-label={t("staffActivities.backlog.sprintForm.start")} />
                </div>
                <div>
                  <label className={label}>{t("staffActivities.backlog.sprintForm.end")} *</label>
                  <input type="date" value={sprintForm.endDate} onChange={(e) => setSprintForm({ ...sprintForm, endDate: e.target.value })} className={`${field} w-full`} aria-label={t("staffActivities.backlog.sprintForm.end")} />
                </div>
              </div>
              {sprintError && <p className="text-sm text-red-500">{sprintError}</p>}
              <div className="flex justify-end gap-2 border-t pt-3 dark:border-dark-border">
                <button type="button" onClick={() => setSprintForm(null)} disabled={savingSprint} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text} ${tc.buttonHover}`}>
                  {t("staffActivities.common.cancel")}
                </button>
                <button type="button" onClick={() => void saveSprint()} disabled={savingSprint} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-500 disabled:opacity-60">
                  {t("staffActivities.backlog.sprintForm.create")}
                </button>
              </div>
            </div>
          }
        />
      )}
      {creating && (
        <ActividadFormModal
          mode="create"
          catalog={catalog}
          sprints={sprints}
          epics={epics}
          onClose={() => setCreating(false)}
          onSaved={(a) => {
            setCreating(false);
            if (!a.sprintId) setRows((prev) => [a, ...prev.filter((x) => x.id !== a.id)]);
            setAllRows((prev) => [a, ...prev.filter((x) => x.id !== a.id)]);
          }}
        />
      )}
      {detail && (
        <ActividadDetailModal
          activity={detail}
          catalog={catalog}
          sprints={sprints}
          epics={epics}
          isOwner={isOwner}
          canManage={canManage}
          onClose={() => setDetail(null)}
          onChanged={(next) => {
            setRows((prev) => (next && !next.sprintId && next.status !== "done" ? prev.map((a) => (a.id === detail.id ? { ...a, ...next } : a)) : prev.filter((a) => a.id !== detail.id)));
            if (next) setDetail(next);
          }}
        />
      )}
    </div>
  );
}
